import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// CIO / "Today" data layer. Thin react-query wrappers over the live role RPCs.
// Row shapes are permissive (documented fields + an index signature) so screens can
// read named fields type-safely without coupling to the generated `Json` returns.

type Row = Record<string, any>;

export interface OversightSummary extends Row {
  n_theses?: number;
  n_single_source?: number;
  single_source_pct?: number;
  n_drift_flagged?: number;
  n_coverage_gaps?: number;
  n_open_conflicts?: number; // the large informal count — do NOT surface
}
export interface OversightCalibration extends Row {
  hit_rate?: number;
  n_evaluated?: number;
  n_predictions?: number;
  mean_abs_error?: number;
  mean_error_pct?: number; // do NOT surface
}
export interface OversightConflictSla extends Row {
  n_open_formal?: number;
  n_sla_breach?: number;
  sla_days?: number;
}
export interface OversightPanel extends Row {
  as_of?: string;
  generated_at?: string;
  summary?: OversightSummary;
  calibration?: OversightCalibration;
  conflict_sla?: OversightConflictSla;
  thesis_review_queue?: Row[];
  coverage_gaps?: Row[];
  open_conflicts?: Row[];
  inconsistencies?: Row[];
}

// Keep _limit small (12) on the home screen — the full panel at 25 is ~130KB.
export function useCioOversightPanel(limit = 12) {
  return useQuery({
    queryKey: ["research_cio_oversight_panel", limit],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_oversight_panel", {
        _as_of: new Date().toISOString(),
        _limit: limit,
      });
      if (error) throw error;
      return (data ?? {}) as unknown as OversightPanel;
    },
  });
}

export interface ThesisHealthRow extends Row {
  edge_id?: string;
  from_label?: string;
  to_label?: string;
  thesis_direction?: string;
  conviction_score?: number;
  health_status?: string;
  single_source_risk?: boolean;
  staleness_days?: number;
  days_since_evidence?: number;
  drift_alert_id?: string | null;
  divergent_against_edge_id?: string | null;
}
export function useCioThesisHealth(minConviction = 0, limit = 100) {
  return useQuery({
    queryKey: ["research_cio_thesis_health", minConviction, limit],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_thesis_health", {
        _min_conviction: minConviction,
        _limit: limit,
      });
      if (error) throw error;
      return ((data as unknown as ThesisHealthRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface OpenConflictRow extends Row {
  conflict_id?: string;
  a_label?: string;
  b_label?: string;
  a_direction?: string;
  b_direction?: string;
  edge_id_a?: string;
  edge_id_b?: string;
  kind?: string;
  detail?: string;
}
export interface ConflictProposalRow extends Row {
  conflict_id?: string;
  subject_label?: string;
  proposed_winner_edge_id?: string;
  proposed_resolution?: string;
  margin?: number;
  margin_band?: string;
  needs_human?: boolean;
  rationale?: string;
}

export function useCioOpenConflicts() {
  return useQuery({
    queryKey: ["research_cio_open_conflicts"],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_open_conflicts", {});
      if (error) throw error;
      return ((data as unknown as OpenConflictRow[]) ?? []).filter(Boolean);
    },
  });
}

export function useConflictProposals() {
  return useQuery({
    queryKey: ["research_conflict_resolution_proposals"],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_conflict_resolution_proposals", {});
      if (error) throw error;
      return ((data as unknown as ConflictProposalRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface ConflictQueueRow extends Row {
  conflict_id?: string;
  status?: string;
  kind?: string;
  age_days?: number;
  sla_breach?: boolean;
}
export function useConflictQueue(slaDays = 14) {
  return useQuery({
    queryKey: ["research_conflict_queue", slaDays],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_conflict_queue", { _sla_days: slaDays });
      if (error) throw error;
      return ((data as unknown as ConflictQueueRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface CoverageGapRow extends Row {
  fund_id?: string;
  fund_label?: string;
  portfolio?: number;
  weight_pct?: number;
  gap_score?: number;
  no_source?: boolean;
  support_tier?: string;
  newest_source_at?: string;
  effective_date?: string;
  open_conflict_ids?: string[];
}
export function useCoverageGaps(portfolio: number | null = null, limit = 50) {
  return useQuery({
    queryKey: ["research_coverage_gaps", portfolio, limit],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const args: Row = { _limit: limit };
      if (portfolio != null) args._portfolio = portfolio;
      const { data, error } = await supabase.rpc("research_coverage_gaps", args as any);
      if (error) throw error;
      return ((data as unknown as CoverageGapRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface CalibrationSeriesRow extends Row {
  series?: string;
  series_label?: string;
  metric?: string;
  n_evaluated?: number;
  hit_rate?: number;
  mean_abs_error?: number;
}
export interface CalibrationSummary extends Row {
  populated?: boolean;
  note?: string;
  hit_rate?: number;
  n_evaluated?: number;
  n_predictions?: number;
  mean_abs_error?: number;
  mean_error_pct?: number; // do NOT surface
  per_series?: CalibrationSeriesRow[];
}
export function useCioCalibration() {
  return useQuery({
    queryKey: ["research_cio_calibration_summary"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_calibration_summary", {});
      if (error) throw error;
      return (data ?? {}) as unknown as CalibrationSummary;
    },
  });
}

export interface HouseViewConsistencyRow extends Row {
  edge_id?: string;
  from_label?: string;
  to_label?: string;
  thesis_direction?: string;
  verdict?: string;
  contradicted_by_edge_id?: string | null;
}
export function useHouseViewConsistency(minConviction = 0, limit = 200) {
  return useQuery({
    queryKey: ["research_cio_house_view_consistency", minConviction, limit],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_house_view_consistency", {
        _min_conviction: minConviction,
        _limit: limit,
      });
      if (error) throw error;
      return ((data as unknown as HouseViewConsistencyRow[]) ?? []).filter(Boolean);
    },
  });
}

export function useDecisionTrail(tradeOrDecision: string | undefined) {
  return useQuery({
    queryKey: ["research_cio_decision_trail", tradeOrDecision],
    enabled: !!tradeOrDecision,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_cio_decision_trail", {
        _trade_or_decision: tradeOrDecision as string,
      });
      if (error) throw error;
      return (data ?? null) as unknown;
    },
  });
}

// The one write on the CIO surface: apply a conflict resolution (CIO confirm dialog).
export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conflictId,
      resolution,
      note,
    }: {
      conflictId: string;
      resolution: string;
      note?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("research_kg_resolve_conflict", {
        _conflict_id: conflictId,
        _resolution: resolution,
        _note: note ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_cio_open_conflicts"] });
      qc.invalidateQueries({ queryKey: ["research_conflict_resolution_proposals"] });
      qc.invalidateQueries({ queryKey: ["research_conflict_queue"] });
      qc.invalidateQueries({ queryKey: ["research_cio_oversight_panel"] });
    },
  });
}
